/**
 * 等距实体预览虚线与上色轮廓关系判定模块。
 *
 * 职责：承接等距实体"阶段三"的全部逻辑——
 * ① 收集当前活动草图组内所有上色草图轮廓对象；
 * ② 针对每个基础草图对象（线段、圆弧、椭圆弧、样条、抛物线）与轮廓判定关系：
 *    相交则求交点并分割预览实体，包含则整体标记 inside，远离则整体标记 outside；
 * ③ 输出：虚线标记 Map（对象 uuid → inside/outside）与分割段 Map（原始实体 uuid → 预览子段），
 *    供调用方在预览渲染阶段决定实线/虚线。
 *
 * 设计说明：
 * 1. 分割只影响预览模型，不影响最终生成的等距实体（最终实体始终使用原始实体）；
 * 2. 轮廓判定结果与预览实体生命周期绑定，调用方在刷新预览时应调用 reset() 清理。
 */

import type { Viewer } from '../../viewer';
import type { ReferencePlane } from '../../reference';
import {
    SketchLineObject,
    SketchArcObject,
    SketchCircleObject,
    SketchEllipseObject,
    SketchParabolaObject,
    SketchSplineObject,
    SketchStyleSplineObject,
    SketchGroup,
    SketchPointObject,
    SketchPointKind,
    isSketchContour,
    isSketchCollectionObject,
    isSketchSlotObject,
    type SketchContour,
    type SketchArcDefinition,
} from '../../mesh';
import { Box3, EllipseCurve, Vector2, Vector3 } from 'three/webgpu';
import { PointsMaterialFactory } from '../../material';
import { SketchEntityIntersect, type SketchIntersectionCandidate } from './SketchEntityIntersect';
import { SplitEntityByIntersectPoints } from './SplitEntityByIntersectPoints';
import { pointInPolygon2 } from '../../sketch-analysis/SketchRegionMath';
import type { OffsetCandidate, ShadedContourRelation } from './type';

export class SketchEntityPreviewOverlayDashLine {
    /** 上色草图轮廓收集结果（阶段三①，供相交/包含判断使用）。 */
    shadedSketchContours: SketchContour[] = [];

    /** 预览虚线标记：对象 uuid → inside（虚线）/ outside（实线）。 */
    isdashedLineMark = new Map<string, ShadedContourRelation>();

    /** 原始等距实体 uuid → 分割后的预览子段（仅预览用，不入最终实体列表）。 */
    splitSegmentsMap = new Map<string, OffsetCandidate[]>();

    /**
     * 构造轮廓关系判定器。
     * @param viewer 视图门面（用于读取当前活动草图组与参考面）。
     */
    constructor(private readonly viewer: Viewer) {
    }

    /**
     * 获取当前激活草图组的参考面。
     * 说明：从 viewer.sketchGroupManager.activeSketchGroup 动态获取，
     * 确保在草图切换后也能拿到正确的平面。
     */
    get referencePlane(): ReferencePlane | null {
        return this.viewer.sketchGroupManager?.activeSketchGroup?.referencePlane ?? null;
    }

    /**
     * 获取当前激活草图组。
     * @returns 活动草图组；不存在时返回 null。
     */
    get activeSketchGroup(): SketchGroup | null {
        return this.viewer.sketchGroupManager.activeSketchGroup ?? null;
    }

    /**
     * 执行阶段三：收集轮廓 + 逐个实体判定关系并写入标记。
     *
     * 说明：
     * 1. 每次调用都会重新收集轮廓并重建两个 Map（等价于先 reset 再判定）；
     * 2. 该过程只写标记与分割段数据，不触碰场景树。
     *
     * @param entities 待判定的偏移后实体列表。
     */
    process(entities: OffsetCandidate[]): void {
        // ① 收集所有的上色草图轮廓对象
        this.shadedSketchContours = this.collectShadedSketchContours();
        // ② 针对每个基础草图对象与轮廓的关系：相交则求交点、是否包含、是否远离
        this.markEntityContourRelations(entities);
    }

    /**
     * 清空轮廓收集结果与全部标记，供预览刷新与退出模式时调用。
     */
    reset(): void {
        this.shadedSketchContours = [];
        this.isdashedLineMark = new Map();
        this.splitSegmentsMap = new Map();
    }

    /**
     * 收集当前活动草图组内所有上色草图轮廓对象。
     *
     * 说明：
     * 1. 上色草图轮廓是派生显示状态，作为闭合宿主的子节点挂在草图组对象树中；
     * 2. 复用 isSketchContour 类型守卫遍历活动草图组收集，与 clearSketchContourDisplays 同一范式；
     * 3. 收集结果仅在本次预览生成期间有效，不持久化，退出模式时随 reset 清理。
     *
     * @returns 当前活动草图组内的上色草图轮廓对象列表。
     */
    private collectShadedSketchContours(): SketchContour[] {
        const activeGroup = this.activeSketchGroup;
        if (!activeGroup) {
            return [];
        }
        const contours: SketchContour[] = [];
        activeGroup.traverse((object) => {
            if (isSketchContour(object)) {
                contours.push(object);
            }
        });
        return contours;
    }

    // ═══════════════════════════════════════════════════════════
    // 阶段三②：实体 × 上色轮廓 关系判定（相交求交点 / 包含 / 远离）
    // ═══════════════════════════════════════════════════════════

    /**
     * 逐个实体与上色轮廓判定关系并写入标记。
     *
     * 说明：
     * 1. 对每个实体与每个轮廓先做 AABB 包围盒预判：不相交视为远离，直接标记 outside；
     * 2. AABB 相交时与轮廓各组成边精确求交（解析求交优先，圆转满圆弧复用弧求交接口）；
     * 3. 无交点：整段未穿透轮廓，按实体中点包含测试标记 inside/outside；
     * 4. 有交点：按交点分割实体为多段（仅预览用），逐段做中点包含测试标记，
     *    分割段保存到 splitSegmentsMap 供预览渲染使用；
     * 5. 圆实体暂不支持分割（无端点协议），有交点时按整体中点包含测试兜底标记。
     *
     * @param entities 待判定的偏移后实体列表。
     */
    private markEntityContourRelations(entities: OffsetCandidate[]): void {
        for (const entity of entities) {
            const intersections: SketchIntersectionCandidate[] = [];
            let aabbIntersectsAny = false;

            for (const contour of this.shadedSketchContours) {
                // ① AABB 包围盒预判：不相交视为远离该轮廓，跳过精确求交
                const entityBox = new Box3().setFromObject(entity);
                const contourBox = new Box3().setFromObject(contour);
                if (!entityBox.intersectsBox(contourBox)) {
                    continue;
                }
                aabbIntersectsAny = true;

                // ② AABB 相交：与轮廓各组成边精确求交
                for (const edge of this.resolveContourEdges(contour)) {
                    intersections.push(...this.computeEntityEdgeIntersections(entity, edge));
                }
            }

            if (!aabbIntersectsAny) {
                // ③a 远离：与所有轮廓包围盒均不相交，直接标记外部（实线）
                this.isdashedLineMark.set(entity.uuid, 'outside');
                continue;
            }

            if (intersections.length === 0) {
                // ③b 包含/悬空：包围盒相交但无交点，按实体中点包含测试标记
                this.isdashedLineMark.set(
                    entity.uuid,
                    this.isEntityInsideShadedContours(entity) ? 'inside' : 'outside',
                );
                continue;
            }

            // ④ 相交：按交点分割实体，逐段标记（仅影响预览，不影响最终等距实体）
            const segments = this.splitEntityByIntersections(entity, intersections);
            if (segments.length === 0) {
                // 分割失败（如圆实体）时按整体中点包含测试兜底标记
                this.isdashedLineMark.set(
                    entity.uuid,
                    this.isEntityInsideShadedContours(entity) ? 'inside' : 'outside',
                );
                continue;
            }
            this.splitSegmentsMap.set(entity.uuid, segments);
            for (const segment of segments) {
                this.isdashedLineMark.set(
                    segment.uuid,
                    this.isEntityInsideShadedContours(segment) ? 'inside' : 'outside',
                );
            }
        }
    }

    /**
     * 展开轮廓 source 为可参与求交的组成边列表。
     * 说明：集合/槽口是复合宿主，需展开为其组成边；
     * 圆/椭圆/样条等自闭合宿主直接以自身参与求交。
     * @param contour 上色草图轮廓。
     * @returns 可参与求交的轮廓边列表。
     */
    private resolveContourEdges(contour: SketchContour): OffsetCandidate[] {
        const source = contour.source;
        if (isSketchCollectionObject(source)) {
            return Array.from(source.collectSet);
        }
        if (isSketchSlotObject(source)) {
            return [...source.getEdges()];
        }
        return [source as OffsetCandidate];
    }

    /**
     * 计算实体与轮廓边的全部交点。
     * 说明：
     * 1. 常规组合复用 SketchEntityIntersect 解析求交矩阵（15 种两两组合）；
     * 2. 圆不在解析求交矩阵内（computeEntityIntersections 对圆返回空），
     *    将圆转换为满圆弧访问器后复用 SketchEntityIntersect 的弧求交接口。
     * @param entity 偏移后实体。
     * @param edge 轮廓组成边。
     * @returns 全部候选交点。
     */
    private computeEntityEdgeIntersections(entity: OffsetCandidate, edge: OffsetCandidate): SketchIntersectionCandidate[] {
        const isEntityCircle = entity instanceof SketchCircleObject;
        const isEdgeCircle = edge instanceof SketchCircleObject;
        if (!isEntityCircle && !isEdgeCircle) {
            return SketchEntityIntersect.computeEntityIntersections(entity, edge);
        }
        // 圆参与：圆 → 满圆弧访问器，复用弧求交接口
        const circleArcA = isEntityCircle ? this.toFullCircleArc(entity) : null;
        const circleArcB = isEdgeCircle ? this.toFullCircleArc(edge) : null;
        const circleArc = circleArcA ?? circleArcB!;
        const other = isEntityCircle ? edge : entity;

        // 圆 × 圆：两满圆弧求交（同心同半径重合时接口返回空，退化场景可接受）
        if (circleArcA && circleArcB) {
            return SketchEntityIntersect.computeArcArcIntersections(circleArcA, circleArcB);
        }
        // 圆 × 线段
        if (other instanceof SketchLineObject) {
            return SketchEntityIntersect.computeLineArcIntersections(other, circleArc);
        }
        // 圆 × 圆弧
        if (other instanceof SketchArcObject) {
            return SketchEntityIntersect.computeArcArcIntersections(other, circleArc);
        }
        // 圆 × 椭圆弧：接口要求弧作为第一参数
        if (other instanceof SketchEllipseObject) {
            return SketchEntityIntersect.computeArcEllipseIntersections(circleArc, other);
        }
        // 圆 × 样条（含样式样条：接口内部仅访问 getCurvePoints/getCurveMetadata，断言安全）
        if (other instanceof SketchSplineObject || other instanceof SketchStyleSplineObject) {
            return SketchEntityIntersect.computeArcSplineIntersections(
                circleArc,
                other as unknown as SketchSplineObject,
            );
        }
        // 圆 × 抛物线
        if (other instanceof SketchParabolaObject) {
            return SketchEntityIntersect.computeArcParabolaIntersections(circleArc, other);
        }
        return [];
    }

    /**
     * 将圆对象包装为"满圆弧"访问器（鸭子对象）。
     * 说明：
     * 1. SketchEntityIntersect 的弧求交接口内部只访问弧对象的 referencePlane 与 getArcDefinition()；
     * 2. 满圆弧定义 startAngle=0、endAngle=2π，isAngleInArcRange 对整周弧有特判
     *    （跨度 ≥ 2π 恒 true），角度过滤对圆上任意交点恒通过；
     * 3. 用最小对象避免构造真实 SketchArcObject 带来的控制点/几何创建与释放开销。
     * @param circle 圆对象。
     * @returns 可传入 SketchEntityIntersect 弧求交接口的满圆弧访问器。
     */
    private toFullCircleArc(circle: SketchCircleObject): SketchArcObject {
        const arcDefinition: SketchArcDefinition = {
            centerWorld: circle.vec3dCenter.clone(),
            radius: circle.radius,
            startAngle: 0,
            endAngle: Math.PI * 2,
            clockwise: false,
            segments: 64,
        };
        return {
            referencePlane: circle.referencePlane,
            getArcDefinition: () => arcDefinition,
        } as unknown as SketchArcObject;
    }

    /**
     * 采样实体为世界坐标折线点列（交点排序与包含测试共用）。
     * @param entity 草图实体。
     * @returns 世界坐标采样点列；不支持的类型返回空数组。
     */
    private sampleEntityPoints(entity: OffsetCandidate): Vector3[] {
        const refPlane = this.referencePlane;
        if (!refPlane) {
            return [];
        }
        if (entity instanceof SketchLineObject) {
            return [entity.startPoint.position.clone(), entity.endPoint.position.clone()];
        }
        if (entity instanceof SketchCircleObject) {
            // 圆按定义在参考面局部系采样 64 段闭合折线
            const center = entity.vec3dCenter;
            const radius = entity.radius;
            const localCenter = refPlane.worldToLocal(center.clone());
            const points: Vector3[] = [];
            const segmentCount = 64;
            for (let index = 0; index <= segmentCount; index += 1) {
                const angle = (Math.PI * 2 * index) / segmentCount;
                const localPoint = new Vector2(
                    localCenter.x + radius * Math.cos(angle),
                    localCenter.y + radius * Math.sin(angle),
                );
                points.push(refPlane.localToWorld(localPoint));
            }
            return points;
        }
        if (entity instanceof SketchArcObject) {
            const definition = entity.getArcDefinition();
            const localCenter = refPlane.worldToLocal(definition.centerWorld.clone());
            const curve = new EllipseCurve(
                localCenter.x,
                localCenter.y,
                definition.radius,
                definition.radius,
                definition.startAngle,
                definition.endAngle,
                definition.clockwise,
                0,
            );
            return curve.getPoints(definition.segments ?? 64)
                .map((point) => refPlane.localToWorld(new Vector2(point.x, point.y)));
        }
        if (entity instanceof SketchEllipseObject) {
            const definition = entity.getEllipseDefinition();
            const localCenter = refPlane.worldToLocal(definition.centerWorld.clone());
            const curve = new EllipseCurve(
                localCenter.x,
                localCenter.y,
                definition.radiusX,
                definition.radiusY,
                definition.startAngle,
                definition.endAngle,
                definition.clockwise,
                definition.rotation,
            );
            return curve.getPoints(definition.segments ?? 64)
                .map((point) => refPlane.localToWorld(new Vector2(point.x, point.y)));
        }
        if (entity instanceof SketchSplineObject || entity instanceof SketchStyleSplineObject) {
            return entity.getCurvePoints();
        }
        if (entity instanceof SketchParabolaObject) {
            return entity.getCurvePoints();
        }
        return [];
    }

    /**
     * 把世界坐标点投影到折线，返回归一化全局参数（按累计长度，0~1）。
     * @param point 世界坐标点。
     * @param polyline 世界坐标折线。
     * @returns 归一化全局参数。
     */
    private projectToPolylineParameter(point: Vector3, polyline: Vector3[]): number {
        const cumulativeLengths: number[] = [0];
        for (let index = 1; index < polyline.length; index += 1) {
            cumulativeLengths.push(
                cumulativeLengths[index - 1]! + polyline[index]!.distanceTo(polyline[index - 1]!),
            );
        }
        const totalLength = cumulativeLengths[cumulativeLengths.length - 1]!;
        if (totalLength <= 1e-12) {
            return 0;
        }
        let bestSegmentIndex = 0;
        let bestSegmentT = 0;
        let bestDistanceSq = Infinity;
        for (let index = 0; index < polyline.length - 1; index += 1) {
            const segmentDirection = polyline[index + 1]!.clone().sub(polyline[index]!);
            const segmentLengthSq = segmentDirection.lengthSq();
            const segmentT = segmentLengthSq > 1e-24
                ? Math.max(0, Math.min(1,
                    point.clone().sub(polyline[index]!).dot(segmentDirection) / segmentLengthSq))
                : 0;
            const projection = polyline[index]!.clone().addScaledVector(segmentDirection, segmentT);
            const distanceSq = point.distanceToSquared(projection);
            if (distanceSq < bestDistanceSq) {
                bestDistanceSq = distanceSq;
                bestSegmentIndex = index;
                bestSegmentT = segmentT;
            }
        }
        return (cumulativeLengths[bestSegmentIndex]!
            + bestSegmentT * (cumulativeLengths[bestSegmentIndex + 1]! - cumulativeLengths[bestSegmentIndex]!))
            / totalLength;
    }

    /**
     * 将交点按实体折线参数排序去重，构造分割用的有序点集 [起点, 交点..., 终点]。
     * @param entity 待分割实体。
     * @param intersections 实体与轮廓的全部交点。
     * @returns 有序控制点列；实体无端点协议或采样失败时返回 null。
     */
    private buildOrderedSplitPoints(entity: OffsetCandidate, intersections: SketchIntersectionCandidate[]): SketchPointObject[] | null {
        const refPlane = this.referencePlane;
        const startPoint = this.resolveEntityEndpoint(entity, 'start');
        const endPoint = this.resolveEntityEndpoint(entity, 'end');
        if (!refPlane || !startPoint || !endPoint) {
            return null;
        }
        const entityPoints = this.sampleEntityPoints(entity);
        if (entityPoints.length < 2) {
            return null;
        }
        // 交点按实体折线全局参数排序去重
        const unique = this.sortAndDeduplicateIntersections(entityPoints, intersections);
        // 构造 [起点, 交点..., 终点] 有序点集，交点新建独立控制点实例
        const points: SketchPointObject[] = [startPoint];
        for (const item of unique) {
            points.push(new SketchPointObject({
                position: item.worldPoint.clone(),
                pointKind: SketchPointKind.ControlPoint,
                material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
                referencePlane: refPlane,
            }));
        }
        points.push(endPoint);
        return points;
    }

    /**
     * 按交点分割实体为预览子段。
     * 说明：分割只影响预览模型；最终等距实体始终使用原始实体。
     * @param entity 待分割实体。
     * @param intersections 实体与轮廓的全部交点。
     * @returns 分割后的预览子段；类型不支持分割（圆等）时返回空数组。
     */
    private splitEntityByIntersections(entity: OffsetCandidate, intersections: SketchIntersectionCandidate[]): OffsetCandidate[] {
        // 圆为闭合实体无端点协议，走专门的圆周分割路径
        if (entity instanceof SketchCircleObject) {
            return this.splitCircleEntityByIntersections(entity, intersections);
        }
        const points = this.buildOrderedSplitPoints(entity, intersections);
        if (!points || points.length < 2) {
            return [];
        }
        if (entity instanceof SketchLineObject) {
            return SplitEntityByIntersectPoints.splitLineByIntersectPoints(points, entity);
        }
        if (entity instanceof SketchArcObject) {
            return SplitEntityByIntersectPoints.splitArcByIntersectPoints(points, entity);
        }
        if (entity instanceof SketchEllipseObject) {
            return SplitEntityByIntersectPoints.splitEllipseByIntersectPoints(points, entity);
        }
        if (entity instanceof SketchSplineObject) {
            return SplitEntityByIntersectPoints.splitSplineByIntersectPoints(points, entity);
        }
        if (entity instanceof SketchParabolaObject) {
            return SplitEntityByIntersectPoints.splitParabolaByIntersectPoints(points, entity);
        }
        // 圆/样式样条暂不支持分割：返回空数组走整体标记兜底
        return [];
    }

    /**
     * 按交点分割圆实体为预览子弧。
     *
     * 说明：
     * 1. 圆为闭合实体无端点协议，交点按圆周采样折线参数排序后
     *    两两配对切出子弧（最后一段回绕到首点），仅用于预览；
     * 2. 交点不足两个（相切等）时返回空数组，由调用方走整体中点标记兜底。
     *
     * @param entity 圆实体。
     * @param intersections 实体与轮廓的全部交点。
     * @returns 分割后的预览子弧；交点不足两个时返回空数组。
     */
    private splitCircleEntityByIntersections(entity: SketchCircleObject, intersections: SketchIntersectionCandidate[]): OffsetCandidate[] {
        const refPlane = this.referencePlane;
        if (!refPlane) {
            return [];
        }
        const entityPoints = this.sampleEntityPoints(entity);
        if (entityPoints.length < 2) {
            return [];
        }
        // 交点按圆周折线参数排序去重
        const ordered = this.sortAndDeduplicateIntersections(entityPoints, intersections);
        if (ordered.length < 2) {
            return [];
        }
        // 交点新建独立控制点实例，构成圆周有序点集
        const points: SketchPointObject[] = ordered.map((item) => new SketchPointObject({
            position: item.worldPoint.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane: refPlane,
        }));
        return SplitEntityByIntersectPoints.splitCircleByIntersectPoints(points, entity);
    }

    /**
     * 将交点按实体采样折线参数排序去重。
     * 说明：排序键为交点投影到折线的归一化全局参数（按累计长度），
     * 相邻交点参数差小于 1e-6 视为同一交点；排序结果供分割点集构造共用。
     * @param entityPoints 实体采样折线（世界坐标）。
     * @param intersections 全部交点。
     * @returns 排序去重后的交点（含折线参数与世界坐标）。
     */
    private sortAndDeduplicateIntersections(
        entityPoints: Vector3[],
        intersections: SketchIntersectionCandidate[],
    ): { parameter: number; worldPoint: Vector3 }[] {
        const sorted = intersections
            .map((candidate) => ({
                parameter: this.projectToPolylineParameter(candidate.worldPoint, entityPoints),
                worldPoint: candidate.worldPoint,
            }))
            .sort((left, right) => left.parameter - right.parameter);
        // 容差去重：相邻交点参数差过小视为同一交点
        const unique: { parameter: number; worldPoint: Vector3 }[] = [];
        for (const item of sorted) {
            const previous = unique[unique.length - 1] ?? null;
            if (!previous || Math.abs(item.parameter - previous.parameter) > 1e-6) {
                unique.push(item);
            }
        }
        return unique;
    }

    /**
     * 判断实体是否位于任一上色轮廓内部（射线法点包含测试）。
     * 说明：以实体采样点的中点作为代表点，投影到参考面 2D 后
     * 对每个轮廓的 getContourPoints 点环做点包含测试。
     * @param entity 待判定实体。
     * @returns 位于任一轮廓内部返回 true。
     */
    private isEntityInsideShadedContours(entity: OffsetCandidate): boolean {
        const refPlane = this.referencePlane;
        if (!refPlane || this.shadedSketchContours.length === 0) {
            return false;
        }
        const entityPoints = this.sampleEntityPoints(entity);
        if (entityPoints.length === 0) {
            return false;
        }
        // 实体采样点列的中点作为代表点：
        // 偶数长度折线（如线段的 [起点, 终点]）取中间两点插值中点，
        // 避免取到端点/交点（落在轮廓边界上）导致射线法判定不稳定；
        // 奇数长度（如 65 点圆采样）取正中点。
        const midIndex = Math.floor((entityPoints.length - 1) / 2);
        const midPoint = entityPoints.length % 2 === 0
            ? entityPoints[midIndex]!.clone().lerp(entityPoints[midIndex + 1]!, 0.5)
            : entityPoints[midIndex]!.clone();
        const testLocal = refPlane.worldToLocal(midPoint.clone());
        for (const contour of this.shadedSketchContours) {
            const contourPoints = contour.getContourPoints();
            if (contourPoints.length < 3) {
                continue;
            }
            const polygon = contourPoints.map((point) => {
                const local = refPlane.worldToLocal(point.clone());
                return [local.x, local.y] as [number, number];
            });
            if (pointInPolygon2([testLocal.x, testLocal.y], polygon)) {
                return true;
            }
        }
        return false;
    }

    /**
     * 解析实体的起点/终点控制点对象。
     * @param entity 草图实体。
     * @param side 起点或终点。
     * @returns 端点控制点；圆等无端点协议类型返回 null。
     */
    private resolveEntityEndpoint(entity: OffsetCandidate, side: 'start' | 'end'): SketchPointObject | null {
        if (entity instanceof SketchLineObject || entity instanceof SketchSplineObject || entity instanceof SketchStyleSplineObject) {
            return side === 'start' ? entity.startPoint : entity.endPoint;
        }
        if (entity instanceof SketchArcObject || entity instanceof SketchEllipseObject || entity instanceof SketchParabolaObject) {
            return side === 'start' ? entity.getStartPoint() : entity.getEndPoint();
        }
        return null;
    }
}
